import type { ClientGeoArea } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useQuery } from '@tanstack/react-query';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import { useGrapevineOrganizationApi } from '@/features/api/hooks/grapevine-organization/useGrapevineOrganizationApi';

export const useGetGeoAreaTree = (organizationId?: string, depth?: number) => {
  const api = useGrapevineOrganizationApi();

  return useQuery({
    queryKey: [API_CACHE_KEYS.GEO_AREAS, organizationId, depth],
    queryFn: () =>
      api.getOrganizationGeoAreas({
        organizationId: organizationId!,
        depth
      }),
    enabled: Boolean(organizationId)
  });
};

export const findNodeById = (
  geoAreas: ClientGeoArea[] | undefined,
  id: string
): ClientGeoArea | undefined => {
  if (!geoAreas) return undefined;
  for (const area of geoAreas) {
    if (area.id === id) return area;
    const found = findNodeById(area.childGeoAreas ?? undefined, id);
    if (found) return found;
  }
  return undefined;
};

export const getAllDescendantIds = (geoArea: ClientGeoArea): string[] => {
  const ids: string[] = [];
  const collect = (area: ClientGeoArea) => {
    ids.push(area.id);
    for (const child of area.childGeoAreas ?? []) {
      collect(child);
    }
  };
  collect(geoArea);
  return ids;
};

/**
 * Returns the ids of all ancestors of the node with the given id (path from
 * the root down to — but excluding — the node itself). Empty when the node is
 * a root or not found.
 */
export const getAncestorIds = (
  geoAreas: ClientGeoArea[] | undefined,
  id: string
): string[] => {
  const find = (
    nodes: ClientGeoArea[] | undefined,
    path: string[]
  ): string[] | undefined => {
    for (const node of nodes ?? []) {
      if (node.id === id) return path;
      const found = find(node.childGeoAreas ?? undefined, [...path, node.id]);
      if (found) return found;
    }
    return undefined;
  };
  return find(geoAreas, []) ?? [];
};

/**
 * Builds a flat `id -> name` lookup for every node in the given geo area tree(s),
 * recursing into all child areas.
 */
export const buildGeoAreaNameMap = (
  geoAreas: ClientGeoArea[] | undefined
): Map<string, string> => {
  const map = new Map<string, string>();
  const walk = (nodes: ClientGeoArea[]) => {
    for (const node of nodes) {
      if (node.id && node.name) map.set(node.id, node.name);
      if (node.childGeoAreas) walk(node.childGeoAreas);
    }
  };
  if (geoAreas) walk(geoAreas);
  return map;
};

/**
 * Collects the leaf areas ("Orte" — areas without children) of the given tree(s).
 */
export const getLeafAreas = (
  geoAreas: ClientGeoArea[] | undefined
): ClientGeoArea[] => {
  const leaves: ClientGeoArea[] = [];
  const walk = (node: ClientGeoArea) => {
    const children = node.childGeoAreas ?? [];
    if (children.length === 0) {
      leaves.push(node);
      return;
    }
    for (const child of children) walk(child);
  };
  for (const root of geoAreas ?? []) walk(root);
  return leaves;
};

/**
 * Counts how many leaf areas ("Orte") a selection covers. Selecting a whole
 * (sub-)area counts all of its leaf descendants; selecting individual leaves
 * counts each one. Used for the "x of y places" visibility summary.
 */
export const countSelectedLeafAreas = (
  geoAreas: ClientGeoArea[] | undefined,
  selectedIds: string[]
): number => {
  const selected = new Set(selectedIds);
  let count = 0;
  const walk = (node: ClientGeoArea, coveredByAncestor: boolean) => {
    const covered = coveredByAncestor || selected.has(node.id);
    const children = node.childGeoAreas ?? [];
    if (children.length === 0) {
      if (covered) count += 1;
      return;
    }
    for (const child of children) walk(child, covered);
  };
  for (const root of geoAreas ?? []) walk(root, false);
  return count;
};

export const hasSelectableGeoAreas = (
  geoAreas: ClientGeoArea[] | undefined
): boolean => getLeafAreas(geoAreas).length > 1;
